import React from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import PriceBadge from '@/components/ui/PriceBadge';
import { TOP_DROPS, TOP_RISES } from '@/data/marketData';

interface PriceMoversSectionProps {
  lang: 'en' | 'ta';
}

function MoverCard({ product, lang }: { product: typeof TOP_DROPS[0]; lang: 'en' | 'ta' }) {
  return (
    <Link href={`/product-detail?id=${product.id}`} className="block group">
      <div className="card-base card-hover p-4 flex items-center gap-4">
        <div className="w-14 h-14 rounded-2xl overflow-hidden bg-muted flex-shrink-0">
          <AppImage
            src={product.image}
            alt={product.imageAlt}
            width={56}
            height={56}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
          />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-foreground truncate">
            {lang === 'en' ? product.name : product.nameTa}
          </p>
          <p className="text-xs text-muted-foreground truncate">
            {lang === 'en' ? product.nameTa : product.name}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <span className="price-value text-base">₹{product.currentPrice}</span>
            <span className="text-xs text-muted-foreground line-through font-tabular">₹{product.previousPrice}</span>
          </div>
        </div>
        <PriceBadge trend={product.trend} change={product.priceChange} changePct={product.priceChangePct} />
      </div>
    </Link>
  );
}

export default function PriceMoversSection({ lang }: PriceMoversSectionProps) {
  return (
    <section>
      <div className="grid md:grid-cols-2 gap-6">
        {/* Drops */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="text-xl">📉</span>
              <h2 className="text-lg font-bold text-foreground">
                {lang === 'en' ? "Today's Biggest Drops" : 'இன்றைய அதிக வீழ்ச்சி'}
              </h2>
            </div>
            <span className="section-label text-drop-green">SAVE MORE</span>
          </div>
          <div className="space-y-2">
            {TOP_DROPS.map(p => (
              <MoverCard key={`drop-${p.id}`} product={p} lang={lang} />
            ))}
          </div>
        </div>

        {/* Rises */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="text-xl">📈</span>
              <h2 className="text-lg font-bold text-foreground">
                {lang === 'en' ? "Today's Biggest Rises" : 'இன்றைய அதிக உயர்வு'}
              </h2>
            </div>
            <span className="section-label text-rise-red">WATCH OUT</span>
          </div>
          <div className="space-y-2">
            {TOP_RISES.map(p => (
              <MoverCard key={`rise-${p.id}`} product={p} lang={lang} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}